import { camelCase, merge, mergeWith } from "lodash";
import React from "react";
import IconBuyerDemand from "../../icons/IconBuyerDemand";
import IconCrossMarketDemand from "../../icons/IconCrossMarketDemand";
import IconHomeCheck from "../../icons/IconHomeCheck";
import IconMatchingBuyers from "../../icons/IconMatchingBuyers";
import IconMegaphone from "../../icons/IconMegaphone";
import IconToolkit from "../../icons/IconToolkit";
import LivePreviewRdcBuyerDemand from "../previews/LivePreviewRdcBuyerDemand";
import LivePreviewRdcCrossMarketDemand from "../previews/LivePreviewRdcCrossMarketDemand";
import LivePreviewRdcListingCompleteness from "../previews/LivePreviewRdcListingCompleteness";
import LivePreviewRdcListingPromotion from "../previews/LivePreviewRdcListingPromotion";
import LivePreviewRdcListingToolkit from "../previews/LivePreviewRdcListingToolkit";
import LivePreviewRdcMatchingBuyers from "../previews/LivePreviewRdcMatchingBuyers";
import LiveRdcBuyerDemand from "../rdc/LiveRdcBuyerDemand";
import LiveRdcCrossMarketDemand from "../rdc/LiveRdcCrossMarketDemand";
import LiveRdcListingCompleteness from "../rdc/LiveRdcListingCompleteness";
import LiveRdcListingPromotion from "../rdc/LiveRdcListingPromotion";
import LiveRdcListingToolkit from "../rdc/LiveRdcListingToolkit";
import LiveRdcMatchingBuyers from "../rdc/LiveRdcMatchingBuyers";

export const createModules = ({
  guid,
  links = {},
  pages = [],
  isWebLead,
  canViewModules
}) => {
  const pageMap = pages.reduce(
    (byKey, page) => ({ ...byKey, [page.key]: page }),
    {}
  );
  const modules = !canViewModules
    ? []
    : [
        {
          key: "listing-toolkit",
          title: "The Realtor.com®; Listing Toolkit",
          url: `/live/${guid}/listing-toolkit`,
          path: "/live/:guid/listing-toolkit",
          isHidden: false,
          canHide: true,
          subModules: []
        },
        {
          key: "listing-promotion",
          title: "Listing Promotion",
          url: `/live/${guid}/listing-promotion`,
          path: "/live/:guid/listing-promotion",
          isHidden: false,
          canHide: true,
          subModules: []
        },
        {
          key: "listing-completeness",
          title: "Listing Completeness",
          url: `/live/${guid}/listing-completeness`,
          path: "/live/:guid/listing-completeness",
          isHidden: false,
          canHide: true,
          subModules: []
        },
        {
          key: "matching-buyers",
          title: "Matching Buyers",
          url: `/live/${guid}/matching-buyers`,
          path: "/live/:guid/matching-buyers",
          isHidden: false,
          canHide: true,
          subModules: []
        },
        {
          key: "buyer-demand",
          title: "Buyer Demand",
          url: `/live/${guid}/buyer-demand`,
          path: "/live/:guid/buyer-demand",
          isHidden: false,
          canHide: true,
          subModules: []
        },
        {
          key: "cross-market-demand",
          title: "Cross-Market Demand",
          url: `/live/${guid}/cross-market-demand`,
          path: "/live/:guid/cross-market-demand",
          isHidden: false,
          canHide: true,
          subModules: []
        }
      ]
        .filter((module) => (isWebLead ? !!pageMap[module.key] : true))
        .map((module) => {
          const page = pageMap[module.key];
          return mergeWith({}, module, page, (a, b) => b ?? a);
        });
  const modifiedLinks = getModifiedLinks(modules, links);
  const newModules = modules.map((module) => {
    const transformedModule = transformModule(module);
    const camelCaseKey = camelCase(transformedModule.key);
    const linkData = modifiedLinks[camelCaseKey] || {};
    const { key, ...linkDataWithoutKey } = linkData;
    const page = pages.find((page) => page.key === module.key) || {};

    return {
      ...transformedModule,
      ...linkDataWithoutKey,
      config: page.config || {}
    };
  });
  return newModules;
};

export function transformModule(data) {
  return {
    key: data.key,
    title: data.title,
    footerTitle: data.footerTitle ?? data.title,
    url: data.url,
    path: data.path,
    isHidden: data.isHidden,
    canHide: data.canHide ?? true,
    el: getElement(data),
    previewEl: getPreviewElement(data),
    iconEl: getIconElement(data),
    showSparkle: true,
    subModules: [],
    data
  };
}

// Only a stub for now
export function transformSubModule(data) {
  return data;
}

function getModifiedLinks(data, links) {
  const newLinks = data.reduce((byKey, page) => {
    const camelCaseKey = camelCase(page.key);
    return {
      ...byKey,
      [page.key]: {
        isHidden: !!(links[camelCaseKey] || {}).isHidden
      }
    };
  }, {});
  return merge({}, links, newLinks);
}

function getElement(page) {
  switch (page.key) {
    case "listing-toolkit":
      return <LiveRdcListingToolkit />;
    case "listing-promotion":
      return <LiveRdcListingPromotion />;
    case "listing-completeness":
      return <LiveRdcListingCompleteness />;
    case "matching-buyers":
      return <LiveRdcMatchingBuyers />;
    case "buyer-demand":
      return <LiveRdcBuyerDemand />;
    case "cross-market-demand":
      return <LiveRdcCrossMarketDemand />;
    default:
      throw new Error("Invalid key");
  }
}

function getPreviewElement(page) {
  switch (page.key) {
    case "listing-toolkit":
      return <LivePreviewRdcListingToolkit title={page.title} />;
    case "listing-promotion":
      return <LivePreviewRdcListingPromotion title={page.title} />;
    case "listing-completeness":
      return <LivePreviewRdcListingCompleteness title={page.title} />;
    case "matching-buyers":
      return <LivePreviewRdcMatchingBuyers title={page.title} />;
    case "buyer-demand":
      return <LivePreviewRdcBuyerDemand title={page.title} />;
    case "cross-market-demand":
      return <LivePreviewRdcCrossMarketDemand title={page.title} />;
    default:
      throw new Error("Invalid key");
  }
}

function getIconElement(page) {
  switch (page.key) {
    case "listing-toolkit":
      return <IconToolkit />;
    case "listing-promotion":
      return <IconMegaphone />;
    case "listing-completeness":
      return <IconHomeCheck />;
    case "matching-buyers":
      return <IconMatchingBuyers />;
    case "buyer-demand":
      return <IconBuyerDemand />;
    case "cross-market-demand":
      return <IconCrossMarketDemand />;
    default:
      throw new Error("Invalid key");
  }
}
